import React, { useEffect, useState } from 'react';
import './ShipmentDetails.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const buildFormState = (shipment, packing_list) => ({
  shesha_reference: shipment?.shesha_reference || '',
  client_name: shipment?.client_name || '',
  delivery_address: shipment?.delivery_address || '',
  sfr_number: shipment?.sfr_number || '',
  bill_of_lading: shipment?.bill_of_lading || '',
  vessel_voyage_number: shipment?.vessel_voyage_number || '',
  supplier_id: packing_list?.supplier_id ? String(packing_list.supplier_id) : '',
  order_number: packing_list?.order_number || '',
  invoice_number: packing_list?.invoice_number || '',
  client_po_number: packing_list?.client_po_number || '',
});

export default function ShipmentDetails({
  shipment,
  packing_list,
  loading = false,
  canEdit = false,
  token,
  onUpdated,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => buildFormState(shipment, packing_list));
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const showPackingListFields = Boolean(packing_list);

  useEffect(() => {
    setForm(buildFormState(shipment, packing_list));
    setIsEditing(false);
    setError('');
    setSuccess('');
  }, [shipment, packing_list]);

  useEffect(() => {
    if (!canEdit || !token || !isEditing) {
      return;
    }
    fetch(`${API_URL}/suppliers`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        if (packing_list?.supplier && !list.some((s) => s.supplier_id === packing_list.supplier.supplier_id)) {
          list.unshift(packing_list.supplier);
        }
        setSuppliers(list);
      })
      .catch(() => {
        setSuppliers(packing_list?.supplier ? [packing_list.supplier] : []);
      });
  }, [canEdit, token, isEditing, packing_list]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setForm(buildFormState(shipment, packing_list));
    setIsEditing(false);
    setError('');
  };

  const handleSave = async () => {
    if (!token || !shipment?.shipment_id) {
      return;
    }

    const payload = {
      shesha_reference: form.shesha_reference.trim(),
      client_name: form.client_name.trim(),
      delivery_address: form.delivery_address,
      sfr_number: form.sfr_number.trim(),
      bill_of_lading: form.bill_of_lading.trim(),
      vessel_voyage_number: form.vessel_voyage_number.trim(),
    };

    if (showPackingListFields) {
      payload.supplier_id = form.supplier_id ? Number(form.supplier_id) : null;
      payload.order_number = form.order_number.trim();
      payload.invoice_number = form.invoice_number.trim();
      payload.client_po_number = form.client_po_number.trim();
    }

    try {
      setSaving(true);
      setError('');
      const url = showPackingListFields
        ? `${API_URL}/packing-lists/${packing_list.packing_list_id}/admin-context`
        : `${API_URL}/shipments/${shipment.shipment_id}/admin-context`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update shipment details');
      }

      setSuccess(
        data.delivery_notes_updated
          ? `Updated. ${data.delivery_notes_updated} delivery note(s) synced.`
          : 'Shipment details updated.'
      );
      setIsEditing(false);
      if (onUpdated) {
        onUpdated(data);
      }
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="shipment-details-block"><p>Loading shipment details...</p></div>;
  }

  if (!shipment) {
    return <div className="shipment-details-block"><p>No shipment details available</p></div>;
  }

  const renderField = (label, name, value, options = {}) => {
    const { textarea = false, required = false, placeholder = '' } = options;

    if (isEditing) {
      return (
        <div className="detail-item" key={name}>
          <label htmlFor={`shipment-${name}`}>{label}</label>
          {textarea ? (
            <textarea
              id={`shipment-${name}`}
              name={name}
              value={value}
              onChange={handleChange}
              rows={3}
              required={required}
            />
          ) : (
            <input
              id={`shipment-${name}`}
              type="text"
              name={name}
              value={value}
              onChange={handleChange}
              required={required}
              placeholder={placeholder}
            />
          )}
        </div>
      );
    }

    return (
      <div className="detail-item" key={name}>
        <label>{label}</label>
        <span className={textarea ? 'address-span' : undefined}>{value || 'N/A'}</span>
      </div>
    );
  };

  return (
    <div className="shipment-details-block">
      <div className="shipment-details-header">
        <h3>Shipment Details</h3>
        {canEdit && !isEditing && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)}>
            Edit
          </button>
        )}
      </div>

      {error && <div className="shipment-details-alert error">{error}</div>}
      {success && <div className="shipment-details-alert success">{success}</div>}

      <div className="details-grid">
        {renderField('Shesha Reference', 'shesha_reference', form.shesha_reference, { required: true })}
        {renderField('Client Name', 'client_name', form.client_name)}
        {renderField('Delivery Address', 'delivery_address', form.delivery_address, { textarea: true })}
        {renderField('Client Ref/PO', 'sfr_number', form.sfr_number)}
        {renderField('Bill of Lading', 'bill_of_lading', form.bill_of_lading)}
        {renderField('Vessel/Flight Number', 'vessel_voyage_number', form.vessel_voyage_number)}

        {showPackingListFields && (
          <>
            {isEditing ? (
              <div className="detail-item">
                <label htmlFor="shipment-supplier_id">Supplier Name</label>
                <select
                  id="shipment-supplier_id"
                  name="supplier_id"
                  value={form.supplier_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.supplier_id} value={supplier.supplier_id}>
                      {supplier.supplier_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              renderField(
                'Supplier Name',
                'supplier_name_display',
                packing_list.supplier?.supplier_name
              )
            )}
            {renderField('Shesha Note', 'order_number', form.order_number, {
              placeholder: 'Internal info only',
            })}
            {renderField('Invoice Number', 'invoice_number', form.invoice_number, { required: true })}
            {renderField('Supplier Ref/PO', 'client_po_number', form.client_po_number)}
          </>
        )}
      </div>

      {isEditing && (
        <div className="shipment-details-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <p className="shipment-details-help">
            Changes apply to this shipment, packing list, and all related delivery notes and PDFs.
          </p>
        </div>
      )}
    </div>
  );
}
